import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextLink from 'LuxKit/TextLink'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import type { PropsWithChildren, ReactNode } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'

const Container = styled.div`
  grid-area: details;
`

const UpgradedToLabel = styled(BodyText)`
  ${tierDarkForegroundColorStyle}
`

type LoyaltyUpgradeTarget = { name: string }

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  optionName?: string
  tagLineLabel?: ReactNode
  /**
   * Optional label rendered above the option title (e.g. rebrand suggested-package Popular badge).
   */
  promotionLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  selectedLoyaltyTargetUpgrade?: LoyaltyUpgradeTarget
  upgradeTitleVariant?: 'default' | 'packageUpgrade' | 'roomUpgrade'
  /**
   * Opens the room details modal for the upgraded room. Only used by the room upgrade variant --
   * a package upgrade keeps the same room, so there is no second room to show.
   */
  onViewLoyaltyUpgradedRoomDetails?: () => void
}

function AccommodationSameRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    tagLineLabel,
    promotionLabel,
    optionName,
    stayPayRateTier,
    selectedLoyaltyTargetUpgrade,
    upgradeTitleVariant = 'default',
    onViewLoyaltyUpgradedRoomDetails,
  } = props

  const intl = useIntl()

  const targetUpgradeName = selectedLoyaltyTargetUpgrade?.name
  const hasLoyaltyUpgrade = !!targetUpgradeName
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const showUpgradedRoomDetailsLink = hasLoyaltyUpgrade && upgradeTitleVariant === 'roomUpgrade' && !!onViewLoyaltyUpgradedRoomDetails

  const packageOptionLabel = packageNumber
    ? intl.formatMessage(
        {
          id: 'e3D2ed1NEgPjpp9ZFe0m',
          defaultMessage: 'Option {number}',
          description: 'Label showing the package option number in the same-room option banner.',
        },
        { number: packageNumber },
      )
    : undefined

  const viewRoomDetailsLabel = intl.formatMessage({
    id: 'W-7laTMCDOxc3XNUuOGH',
    defaultMessage: 'View room details',
    description: 'Link text to open the room details modal. dialog window',
  })

  const upgradedToLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '2-S47pPpwvcaeurwALuu',
          defaultMessage: 'Upgraded to {upgradeName}',
          description: 'Label indicating the selected loyalty upgrade package name (same-room banner).',
        },
        { upgradeName: targetUpgradeName },
      )
    : undefined

  const optionNameLabel = optionName && stayPayRateTier
    ? intl.formatMessage(
        {
          id: '_zgzlQC7jhOJIxFWViD4',
          defaultMessage: '{name}, {count, plural, one {# night free} other {# nights free}}',
          description: 'Package name with included free nights suffix in same-room option banner.',
        },
        { name: optionName, count: stayPayRateTier.freeNights },
      )
    : optionName

  return <Container>
    <VerticalSpacer gap={12}>
      {promotionLabel}
      <VerticalSpacer gap={4}>
        {!!packageNumber && !hasLoyaltyUpgrade && <Caption variant="large">
          {packageOptionLabel}
        </Caption>}
        {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
        {!!upgradedToLabel && upgradeTitleVariant === 'default' && <BodyText variant="large" weight="bold">
          {upgradedToLabel}
        </BodyText>}
        {!!optionName && <BodyText
          variant="large"
          weight={selectedLoyaltyTargetUpgrade && upgradeTitleVariant !== 'default' ? 'normal' : 'bold'}
          colour={selectedLoyaltyTargetUpgrade ? 'neutral-four' : 'normal-contrast'}
          strikeThrough={!!selectedLoyaltyTargetUpgrade}
        >
          {optionNameLabel}
        </BodyText>}
        {!!selectedLoyaltyTargetUpgrade?.name && upgradeTitleVariant === 'roomUpgrade' && <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="A__Ui49cfpieSMD559wS"
            defaultMessage="<accent>Room upgraded to:</accent> {upgradeName}"
            description="Applied loyalty room upgrade heading on the flash offer page rate tile, naming the upgraded room"
            values={{
              upgradeName: selectedLoyaltyTargetUpgrade.name,
              accent: (chunks: React.ReactNode) => <UpgradedToLabel
                as="span"
                variant="large"
                weight="bold"
                className={`tier-${displayTier}`}
              >
                {chunks}
              </UpgradedToLabel>,
            }}
          />
        </BodyText>}
        {showUpgradedRoomDetailsLink && <TextLink onClick={onViewLoyaltyUpgradedRoomDetails} size="medium" weight="regular">
          {viewRoomDetailsLabel}
        </TextLink>}
        {!!selectedLoyaltyTargetUpgrade?.name && upgradeTitleVariant === 'packageUpgrade' && <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="SFriXD2my7zJE8o8IGBY"
            defaultMessage="<accent>Package upgraded to:</accent> {upgradeName}"
            description="Applied loyalty package upgrade heading on the property offer page rate tile, naming the upgraded package"
            values={{
              upgradeName: selectedLoyaltyTargetUpgrade.name,
              accent: (chunks: React.ReactNode) => <UpgradedToLabel
                as="span"
                variant="large"
                weight="bold"
                className={`tier-${displayTier}`}
              >
                {chunks}
              </UpgradedToLabel>,
            }}
          />
        </BodyText>}
      </VerticalSpacer>
      {children}
    </VerticalSpacer>
  </Container>
}

export default AccommodationSameRoomOptionTileDetails
